import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ListCommanWay, UpdateCommanWay } from "API/commonApi";
import { Button } from "@heroui/react";
import UseToast from "utils/AfToast";
import Spinner from "utils/Spinner";
import moment from "moment";
import { useNavigate } from "react-router-dom";

const Notification: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const [notifications, setNotifications] = useState<any[]>([]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { isLoading, refetch } = useQuery(
    ["NotificationList"],
    () =>
      ListCommanWay(
        "/admin/notification/list",
        {},
        { paginate: 50, sort: { createdAt: -1 } }
      ),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          setNotifications(data.data.data || []);
        }
      },
    }
  );

  const { mutate: markAsRead } = useMutation(
    (id: any) =>
      UpdateCommanWay({
        url: `/admin/notification/update/${id}`,
        data: { isRead: true },
      }),
    {
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          refetch();
          queryClient.invalidateQueries(["UnreadNotificationCount"]);
        }
      },
    }
  );

  const { mutate: markAllRead, isLoading: isMarkingAll } = useMutation(
    () =>
      UpdateCommanWay({
        url: `/admin/notification/updateBulk`,
        data: {
          filter: { isRead: false },
          data: { isRead: true },
        },
      }),
    {
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          UseToast("All notifications marked as read", "success");
          refetch();
          queryClient.invalidateQueries(["UnreadNotificationCount"]);
        } else {
          UseToast(data.message, "error");
        }
      },
    }
  );

  const hasUnread = notifications.some((n: any) => !n.isRead);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Notifications</h2>
        {hasUnread && (
          <Button
            size="sm"
            color="primary"
            variant="flat"
            onClick={() => markAllRead()}
            isLoading={isMarkingAll}
          >
            Mark All Read
          </Button>
        )}
      </div>

      {isLoading ? (
        <Spinner />
      ) : notifications.length === 0 ? (
        <p className="text-center text-gray-500 py-8">No notifications</p>
      ) : (
        <div className="space-y-3">
          {notifications.map((n: any) => (
            <div
              key={n.id}
              className={`p-3 rounded-lg border ${
                n.isRead ? "bg-white border-gray-200" : "bg-blue-50 border-blue-200"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <p className={`text-sm ${n.isRead ? "text-gray-700" : "text-gray-900 font-medium"}`}>
                    {n.title || n.message || "-"}
                  </p>
                  {n.body && n.title && (
                    <p className="text-xs text-gray-600 mt-1">{n.body}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    {n.createdAt
                      ? moment(n.createdAt).isValid()
                        ? moment(n.createdAt).fromNow()
                        : n.createdAt
                      : "-"}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {n.url && (
                    <Button
                      size="sm"
                      variant="flat"
                      color="success"
                      onClick={() => {
                        if (!n.isRead) markAsRead(n.id);
                        navigate(n.url);
                        onClose?.();
                      }}
                    >
                      Open
                    </Button>
                  )}
                  {!n.isRead && (
                    <Button
                      size="sm"
                      variant="flat"
                      color="primary"
                      onClick={() => markAsRead(n.id)}
                    >
                      Mark Read
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Notification;
